import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

export async function startPayHerePayment({
  amount,
  wordCount,
  email,
  doc_id,
  onSuccess,
  onError,
  onClose,
}) {
  // Load PayHere script if not already loaded
  if (!window.payhere) {
    await new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://www.payhere.lk/lib/payhere.js';
      script.async = true;
      script.onload = resolve;
      document.body.appendChild(script);
    });
  }

  try {
    // Get user info from session storage
    const userData = sessionStorage.getItem('user');
    if (!userData) {
      throw new Error('User not authenticated');
    }
    const userInfo = JSON.parse(userData);

    const formattedAmount = Number(amount).toFixed(2);
    const orderId = `ORDER-${Date.now()}`;

    // Create a unique payment reference that includes email
    const paymentRef = `${orderId}_${userInfo.email}`;

    const paymentDetails = {
      order_id: orderId,
      amount: formattedAmount,
      currency: 'LKR',
      email: userInfo.email,
      auth_id: userInfo.authId,
      auth_token: userInfo.authToken,
      payment_ref: paymentRef, // Add payment reference
      notify_url: `${process.env.REACT_APP_NOTIFY_URL || 'http://localhost:4001/payhere/notify'}?email=${encodeURIComponent(userInfo.email)}`, // Add email to notify URL
    };

    const API_URL =
      process.env.REACT_APP_PAYMENTS_SERVER_URL || 'http://localhost:4001';
    console.log('Payment API URL from .env', API_URL);
    const hashResponse = await axios.post(
      `${API_URL}/payment/start`,
      paymentDetails,
      {
        headers: {
          Authorization: `Bearer ${userInfo.authToken}`,
        },
      }
    );

    if (
      !hashResponse.data ||
      !hashResponse.data.hash ||
      !hashResponse.data.merchant_id
    ) {
      throw new Error('Failed to generate payment hash');
    }

    const { hash, merchant_id } = hashResponse.data;
    const isSandbox = process.env.REACT_APP_SANDBOX !== 'false';

    const payment = {
      sandbox: isSandbox,
      merchant_id: merchant_id,
      return_url: `${window.location.href}?payment_ref=${paymentRef}`, // Add payment reference to return URL
      cancel_url: window.location.href,
      notify_url: paymentDetails.notify_url, // Use the notify URL with email parameter
      order_id: orderId,
      items: `Document Translation (${wordCount} words)`,
      amount: formattedAmount,
      currency: 'LKR',
      hash: hash,
      first_name: 'Document',
      last_name: 'Translation',
      email: userInfo.email,
      phone: '',
      address: '',
      city: '',
      country: 'Sri Lanka',
      custom_1: `word_count:${wordCount}`,
      custom_2: userInfo.email, // Store email in custom_2 for backend reference
      custom_3: paymentRef, // Store payment reference in custom_3
    };

    // Log payment details for debugging
    console.log('Payment details:', {
      orderId,
      email: userInfo.email,
      paymentRef,
      notifyUrl: payment.notify_url,
      customFields: {
        custom_1: payment.custom_1,
        custom_2: payment.custom_2,
        custom_3: payment.custom_3,
      },
    });

    window.payhere.onCompleted = function (orderId) {
      console.log('Payment completed for order:', orderId);
      if (onSuccess) onSuccess(orderId);
    };
    window.payhere.onDismissed = function () {
      console.log('Payment dismissed');
      if (onClose) onClose();
    };
    window.payhere.onError = function (error) {
      console.error('Payment error:', error);
      if (onError) onError(error);
      alert('Payment error: ' + error);
    };

    window.payhere.startPayment(payment);
  } catch (error) {
    console.error('Payment initialization error:', error);
    if (onError) onError(error);
    alert('There was an error initializing the payment. Please try again.');
  }
}

const Payment = ({ amount, wordCount, email, onSuccess, onClose }) => {
  const [payHereLoaded, setPayHereLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('initializing');

  // Load PayHere script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.payhere.lk/lib/payhere.js';
    script.async = true;
    script.onload = () => setPayHereLoaded(true);
    document.body.appendChild(script);
    return () => {
      try {
        document.body.removeChild(script);
      } catch (err) {
        console.warn('Error removing PayHere script:', err);
      }
    };
  }, []);

  // Function to handle retry
  const retryPayment = useCallback(() => {
    setIsProcessing(false);
    setPaymentStatus('initializing');
    // Small delay to ensure proper reset before retrying
    setTimeout(() => {
      if (payHereLoaded && email) {
        setIsProcessing(true);
        setPaymentStatus('processing');
        startPayHerePayment({
          amount,
          wordCount,
          email,
          onSuccess: (orderId) => {
            setPaymentStatus('success');
            setIsProcessing(false);
            onSuccess(orderId);
          },
          onError: (error) => {
            setPaymentStatus('error');
            setIsProcessing(false);
            console.error('Payment error:', error);
          },
          onClose: () => {
            setIsProcessing(false);
            setPaymentStatus('closed');
            onClose();
          },
        });
      }
    }, 500);
  }, [payHereLoaded, email, amount, wordCount, onSuccess, onClose]);

  // Automatically trigger payment when loaded (only once)
  useEffect(() => {
    if (payHereLoaded && email && !isProcessing) {
      setIsProcessing(true);
      setPaymentStatus('processing');

      startPayHerePayment({
        amount,
        wordCount,
        email,
        onSuccess: (orderId) => {
          setPaymentStatus('success');
          setIsProcessing(false);
          onSuccess(orderId);
        },
        onError: (error) => {
          setPaymentStatus('error');
          setIsProcessing(false);
          console.error('Payment error:', error);
        },
        onClose: () => {
          setIsProcessing(false);
          setPaymentStatus('closed');
          onClose();
        },
      });
    }
  }, [
    payHereLoaded,
    email,
    amount,
    wordCount,
    onSuccess,
    onClose,
    isProcessing,
  ]);

  const getStatusMessage = () => {
    switch (paymentStatus) {
      case 'initializing':
        return 'Initializing payment gateway...';
      case 'processing':
        return 'Processing payment...';
      case 'success':
        return 'Payment successful!';
      case 'error':
        return 'Payment failed. Please try again.';
      case 'closed':
        return 'Payment cancelled.';
      default:
        return 'Initializing...';
    }
  };

  
};

Payment.propTypes = {
  amount: PropTypes.number.isRequired,
  wordCount: PropTypes.number.isRequired,
  email: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default Payment;
